import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api, money, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const EMPTY = { name: "", category: "", price: "", compare_at: "", badge: "", stock: "", image: "", alt_image: "", description: "", featured: false };
const FIELDS = [
  ["name", "Name"], ["category", "Category"], ["price", "Price (USD)", "number"], ["compare_at", "Compare-at price", "number"],
  ["stock", "Stock", "number"], ["badge", "Badge"],
];

function ImageField({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast("Image uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="text-sm">
      <span className="text-xs text-[var(--ink-soft)]">{label}</span>
      <div className="mt-1 flex items-center gap-3">
        <div className="grid h-14 w-12 place-items-center overflow-hidden rounded-lg border border-[var(--line)] bg-white/60">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <Plus size={16} className="text-[var(--ink-soft)]" />}
        </div>
        <label className="cursor-pointer rounded-full border border-[var(--ink)] px-4 py-2 text-xs font-semibold transition-colors hover:bg-[var(--sand)]" data-testid={testid}>
          {busy ? "Uploading…" : value ? "Replace image" : "Upload image"}
          <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={pick} />
        </label>
      </div>
    </div>
  );
}

function ProductForm({ initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { ...f, price: Number(f.price), stock: parseInt(f.stock, 10), compare_at: f.compare_at ? Number(f.compare_at) : null, badge: f.badge || null, alt_image: f.alt_image || null };
    try {
      if (f.id) await api.put(`/admin/products/${f.id}`, body); else await api.post("/admin/products", body);
      toast(f.id ? "Product updated" : "Product created");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="admin-product-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <ImageField label="Product image" value={f.image} onChange={(v) => setF({ ...f, image: v })} testid="admin-product-image-upload" />
        <ImageField label="Hover image" value={f.alt_image} onChange={(v) => setF({ ...f, alt_image: v })} testid="admin-product-altimage-upload" />
      </div>
      {FIELDS.map(([k, label, type]) => (
        <label key={k} className="block text-sm"><span className="text-xs text-[var(--ink-soft)]">{label}</span>
          <input type={type || "text"} step="0.01" required={["name", "category", "price", "stock", "image"].includes(k)} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-[var(--line)] bg-white/60 px-3 outline-none focus:border-[var(--terra)]" data-testid={`admin-product-${k}-input`} />
        </label>
      ))}
      <label className="block text-sm sm:col-span-2"><span className="text-xs text-[var(--ink-soft)]">Description</span>
        <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 w-full rounded-lg border border-[var(--line)] bg-white/60 p-3 outline-none focus:border-[var(--terra)]" data-testid="admin-product-description-input" />
      </label>
      <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="admin-product-featured-switch" /> Show in "Trending" on home page</label>
      <button disabled={busy} className="rounded-full bg-[var(--ink)] py-3 text-sm font-semibold text-[var(--bone)] sm:col-span-2" data-testid="admin-product-save-button">{busy ? "Saving…" : "Save product"}</button>
    </form>
  );
}

export function AdminProducts({ onChange }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/products").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast("Deleted"); load(); onChange?.(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-products">
      <button onClick={() => setEditing(EMPTY)} className="mb-6 flex items-center gap-2 rounded-full bg-[var(--terra)] px-5 py-3 text-sm font-semibold text-[#241a06]" data-testid="admin-add-product-button"><Plus size={16} /> Add product</button>
      <div className="space-y-2">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-2xl border border-[var(--line)] p-3" data-testid={`admin-product-row-${p.slug}`}>
            <img src={p.image} alt={p.name} className="h-14 w-12 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{p.name}</p>
              <p className="text-xs text-[var(--ink-soft)]">{p.category} · {money(p.price)} · <span className={p.stock < 10 ? "font-semibold text-[var(--terra-deep)]" : ""}>{p.stock} in stock</span></p>
            </div>
            <button onClick={() => setEditing({ ...p })} className="grid h-10 w-10 place-items-center rounded-full hover:bg-[var(--sand)]" aria-label="Edit" data-testid={`admin-edit-${p.slug}`}><Pencil size={16} /></button>
            <button onClick={() => remove(p)} className="grid h-10 w-10 place-items-center rounded-full text-red-700 hover:bg-red-50" aria-label="Delete" data-testid={`admin-delete-${p.slug}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-[var(--bone)] sm:max-w-2xl" data-lenis-prevent>
          <DialogHeader><DialogTitle className="font-display text-3xl font-normal">{editing?.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          {editing && <ProductForm initial={editing} onSaved={() => { setEditing(null); load(); onChange?.(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
